import { useEffect, useRef, useState } from 'react'
import { LLMModalRef } from '../llm/llm.modal'
import { useLLMState } from '../llm/llm.provider'
import { Cluster } from '../../../../../generated/graphql'
import { ArticleEditorRef } from '../../article-editor/article-editor'

export function useLLMIntegration(
  cluster: Cluster,
  editorRef: React.RefObject<ArticleEditorRef>,
  initialValues?: {
    content?: string
    model?: string
  }
) {
  const llmModalRef = useRef<LLMModalRef>(null)
  const llmState = useLLMState()
  const openOnInit = useRef(false)
  const [prompt, setPrompt] = useState({
    userPrompt: cluster.topic,
    systemPrompt: '',
  })

  // Set initial LLM content
  useEffect(() => {
    if (!initialValues?.content) {
      return
    }
    editorRef.current?.setEditorContent(initialValues.content)
  }, [initialValues?.content, editorRef])

  // Handle LLM model initialization
  useEffect(() => {
    if (!initialValues?.model || openOnInit.current) {
      return
    }

    llmState.setState({
      model: [initialValues.model],
    })
    llmModalRef.current?.open({
      model: llmState.getLabel(initialValues.model),
      userPrompt: cluster.topic,
      systemPrompt: llmState.getPrompt(initialValues.model),
    })
    openOnInit.current = true
  }, [initialValues?.model, cluster.topic])

  // Update prompt when model changes
  useEffect(() => {
    setPrompt({
      userPrompt: cluster.topic,
      systemPrompt: llmState.getPrompt(llmState.state.model[0]),
    })
  }, [llmState.state.model[0], cluster.topic])

  // Update editor when opinion is generated
  useEffect(() => {
    if (llmState.state.opinion && !llmState.state.loading) {
      editorRef.current?.setEditorContent(llmState.state.opinion)
    }
  }, [llmState.state.opinion, llmState.state.loading])

  const handleGenerate = async (userPrompt: string, systemPrompt: string) => {
    setPrompt({ userPrompt, systemPrompt })
    const { html } = (await editorRef.current?.requestContent()) || { html: '' }
    llmState.generateOpinion(
      cluster.id,
      `User:\n${userPrompt}\nCurrent content:\n${html}\nSystem:\n${systemPrompt}`
    )
    llmModalRef.current?.close()
  }

  const openLLMModal = () => {
    llmModalRef.current?.open({
      model: llmState.getLabel(llmState.state.model[0]),
      userPrompt: prompt.userPrompt,
      systemPrompt: prompt.systemPrompt,
    })
  }

  return {
    llmModalRef,
    llmState,
    prompt,
    handleGenerate,
    openLLMModal,
  }
}
